import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, MessageSquare, ArrowUp, ArrowRight } from 'lucide-react';
import { useLocale } from '../context/LocaleContext';
import { Reveal } from './motion/Reveal';
import { FunctionButton } from './motion/FunctionButton';

const FacebookIcon = () => (
  <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
);
const InstagramIcon = () => (
  <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
);
const LinkedinIcon = () => (
  <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/></svg>
);
const XIcon = () => (
  <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
);

const hasRealValue = (val) => val && String(val).trim().length > 0 && !val.includes('111 222');

const Footer = () => {
  const { t, isRtl } = useLocale();
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    fetch('/api/v1/settings')
      .then(res => res.json())
      .then(data => setSettings(data))
      .catch(() => {
        setSettings({
          company_name_en: 'Prime Business Zone',
          company_name_ur: 'پرائم بزنس زون',
        });
      });
  }, []);

  const currentYear = new Date().getFullYear();
  const socialLinks = [
    { key: 'facebook', href: settings?.facebook, label: 'Facebook', icon: FacebookIcon },
    { key: 'instagram', href: settings?.instagram, label: 'Instagram', icon: InstagramIcon },
    { key: 'linkedin', href: settings?.linkedin, label: 'LinkedIn', icon: LinkedinIcon },
    { key: 'twitter', href: settings?.twitter, label: 'X', icon: XIcon },
  ].filter((item) => hasRealValue(item.href));
  const phone = hasRealValue(settings?.phone) ? settings.phone : '+92 339 2244330';
  const whatsapp = hasRealValue(settings?.whatsapp) ? settings.whatsapp : '+92 339 2244330';
  const salesEmail = 'sales@primebusineszone.com';
  const infoEmail = hasRealValue(settings?.email) ? settings.email : 'info@primebusineszone.com';
  const address = hasRealValue(isRtl ? settings?.address_ur : settings?.address_en)
    ? (isRtl ? settings.address_ur : settings.address_en)
    : (isRtl ? 'شاپ جی-04-05، پرائم آرکیڈ، زمر ویلی، اسلام آباد، پاکستان' : 'Shop G-04-05, Prime Arcade, Zamar Valley, Islamabad, Pakistan');
  const hours = hasRealValue(isRtl ? settings?.working_hours_ur : settings?.working_hours_en)
    ? (isRtl ? settings.working_hours_ur : settings.working_hours_en)
    : (isRtl ? 'پیر تا ہفتہ: صبح 9:00 تا شام 7:00 (سائٹ آفس کھلا ہے)' : 'Mon - Sat: 09:00 AM - 07:00 PM (Site Office Open)');

  return (
    <footer data-tone="dark" className="bg-[#070C12] border-t border-[#B99A5B]/20 text-[#F2EEE6] pt-14 sm:pt-20 pb-10 sm:pb-8 relative overflow-hidden font-sans">
      
      {/* Background radial highlight */}
      <div className="absolute bottom-0 right-0 w-[300px] sm:w-[500px] h-[300px] sm:h-[500px] bg-[#B99A5B]/5 rounded-full filter blur-[100px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-12 relative z-10">
        
        {/* Branding & Philosophy */}
        <Reveal direction="up" delay={0.05} className="flex flex-col gap-4 sm:gap-6">
          <div className="flex items-center gap-3 group">
            <div className="h-10 w-12 sm:h-12 sm:w-14 rounded-[4px] border border-[#B99A5B]/40 bg-[#F2EEE6] p-1 flex items-center justify-center shadow-lg flex-shrink-0 transition-shadow duration-300 group-hover:shadow-[0_0_20px_rgba(185,154,91,0.35)]">
              {settings?.logo ? (
                <img src={settings.logo} alt={isRtl ? settings?.company_name_ur : settings?.company_name_en} className="h-full w-full object-contain mix-blend-multiply" />
              ) : (
                <span className="text-[#0B1118] font-bold text-xs sm:text-sm font-headingEn tracking-widest">PBZ</span>
              )}
            </div>
            <span className="text-[#F2EEE6] font-bold text-base sm:text-lg tracking-wider">
              {isRtl ? settings?.company_name_ur : settings?.company_name_en}
            </span>
          </div>
          <p className="text-xs sm:text-sm text-[#F2EEE6]/70 leading-relaxed">
            {isRtl 
              ? 'پرائم بزنس زون اسلام آباد میں واضح پروجیکٹ معلومات، تعمیراتی پیش رفت اور کمرشل ڈیولپمنٹ پر توجہ دیتا ہے۔'
              : 'Prime Business Zone focuses on commercial development, clear project information and verified construction progress in Islamabad.'
            }
          </p>
          {/* Social Profiles with individual hover response */}
          {socialLinks.length > 0 && (
            <div className="flex items-center gap-3 mt-1">
              {socialLinks.map(({ key, href, label, icon: Icon }) => (
                <motion.a 
                  key={key} 
                  href={href} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  aria-label={label}
                  whileHover={{ scale: 1.12, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  className="w-9 h-9 rounded-full border border-white/10 flex items-center justify-center text-[#F2EEE6]/70 hover:text-[#B99A5B] hover:border-[#B99A5B]/50 transition-colors duration-200 bg-white/[0.02]"
                >
                  <Icon />
                </motion.a>
              ))}
            </div>
          )}
        </Reveal>

        {/* Quick Links */}
        <Reveal direction="up" delay={0.12} className="flex flex-col gap-4 sm:gap-6">
          <span className="text-xs sm:text-sm font-bold uppercase tracking-[0.25em] text-[#B99A5B]">
            {isRtl ? 'اہم روابط' : 'QUICK LINKS'}
          </span>
          <div className="flex flex-col gap-2.5">
            {[
              { to: '/projects', label: isRtl ? 'تمام پروجیکٹس' : 'Commercial Projects' },
              { to: '/investment', label: isRtl ? 'سرمایہ کاری کے مواقع' : 'Investment Opportunities' },
              { to: '/updates', label: isRtl ? 'تعمیراتی پیش رفت' : 'Construction Transparency' },
              { to: '/gallery', label: isRtl ? 'پروجیکٹ گیلری' : 'Curated Gallery' },
              { to: '/about', label: isRtl ? 'ہمارے بارے میں' : 'About Developer' },
              { to: '/contact', label: isRtl ? 'رابطہ اور سائٹ وزٹ' : 'Contact & Site Visit' },
            ].map((link) => (
              <Link 
                key={link.to} 
                to={link.to}
                className="group inline-flex items-center gap-1.5 text-xs sm:text-sm text-[#F2EEE6]/70 hover:text-[#B99A5B] transition-colors py-0.5"
              >
                <span className="relative">
                  {link.label}
                  <span className="absolute left-0 bottom-0 w-0 group-hover:w-full h-[1px] bg-[#B99A5B] transition-all duration-300" />
                </span>
              </Link>
            ))}
          </div>
        </Reveal>

        {/* Landmark Projects */}
        <Reveal direction="up" delay={0.18} className="flex flex-col gap-4 sm:gap-6">
          <span className="text-xs sm:text-sm font-bold uppercase tracking-[0.25em] text-[#B99A5B]">
            {isRtl ? 'اہم منصوبے' : 'LANDMARKS'}
          </span>
          <div className="flex flex-col gap-3">
            <Link to="/projects/prime-mall" className="p-3.5 rounded-xl border border-[#B99A5B]/30 bg-[#0B1118] hover:border-[#B99A5B] transition-all group">
              <div className="flex items-center justify-between">
                <span className="text-xs sm:text-sm font-bold text-[#F2EEE6] group-hover:text-[#B99A5B] transition-colors">Prime Mall</span>
                <span className="text-[9px] font-mono px-2 py-0.5 rounded bg-[#B99A5B]/15 text-[#B99A5B] border border-[#B99A5B]/30 uppercase">Ongoing</span>
              </div>
              <p className="text-[11px] text-[#F2EEE6]/60 mt-1 line-clamp-1">7-Level Commercial Landmark</p>
            </Link>

            <Link to="/projects/prime-arcade" className="p-3.5 rounded-xl border border-white/10 bg-[#0B1118] hover:border-emerald-400/50 transition-all group">
              <div className="flex items-center justify-between">
                <span className="text-xs sm:text-sm font-bold text-[#F2EEE6] group-hover:text-emerald-400 transition-colors">Prime Arcade</span>
                <span className="text-[9px] font-mono px-2 py-0.5 rounded bg-emerald-400/15 text-emerald-400 border border-emerald-400/30 uppercase">Delivered</span>
              </div>
              <p className="text-[11px] text-[#F2EEE6]/60 mt-1 line-clamp-1">Completed Delivery Proof</p>
            </Link>
          </div>
        </Reveal>

        {/* Contact & Address */}
        <Reveal direction="up" delay={0.24} className="flex flex-col gap-4 sm:gap-6">
          <span className="text-xs sm:text-sm font-bold uppercase tracking-[0.25em] text-[#B99A5B]">
            {isRtl ? 'سائٹ آفس اور رابطہ' : 'SITE OFFICE & DESK'}
          </span>
          <div className="flex flex-col gap-3 text-xs sm:text-sm text-[#F2EEE6]/70">
            <div className="flex items-start gap-2.5">
              <MapPin className="w-4 h-4 text-[#B99A5B] shrink-0 mt-0.5" />
              <span className="leading-snug">{address}</span>
            </div>
            <div className="flex items-center gap-2.5">
              <Phone className="w-4 h-4 text-[#B99A5B] shrink-0" />
              <a href={`tel:${phone.replace(/\s+/g, '')}`} className="hover:text-[#B99A5B] transition-colors font-mono">{phone}</a>
            </div>
            <div className="flex items-start gap-2.5">
              <Mail className="w-4 h-4 text-[#B99A5B] shrink-0 mt-0.5" />
              <div className="flex flex-col gap-1">
                <a href={`mailto:${salesEmail}`} className="hover:text-[#B99A5B] transition-colors inline-flex items-center">
                  <span className="text-[10px] uppercase font-mono text-[#B99A5B] mr-1.5 font-bold">Sales:</span>{salesEmail}
                </a>
                <a href={`mailto:${infoEmail}`} className="hover:text-[#B99A5B] transition-colors inline-flex items-center">
                  <span className="text-[10px] uppercase font-mono text-white/50 mr-1.5 font-bold">Info:</span>{infoEmail}
                </a>
              </div>
            </div>
            <div className="text-[11px] font-mono text-[#B99A5B]/80 pt-2 border-t border-white/10 leading-normal">
              {hours}
            </div>
          </div>
        </Reveal>

      </div>

      {/* Bottom Legal Hairline */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 mt-12 sm:mt-16 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-[#F2EEE6]/50">
        <div>
          &copy; {currentYear} Prime Business Zone. All rights reserved.
        </div>
        <div className="flex items-center gap-6">
          <Link to="/privacy-policy" className="hover:text-[#B99A5B] transition-colors">Privacy Policy</Link>
          <Link to="/terms" className="hover:text-[#B99A5B] transition-colors">Terms of Service</Link>
          <Link to="/investment-disclaimer" className="hover:text-[#B99A5B] transition-colors">Disclaimer</Link>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
